/** Action type used for the map's loading state (matches the saga's GET_MAP_LOCATIONS). */
export const GET_MAP_LOCATIONS_ACTION = 'GET_MAP_LOCATIONS';

export const MAP_MARKER_TYPES = {
    CLIENT: 'CLIENT',
    STAFF: 'STAFF',
};

/**
 * Keys of the map's filter state. Same shape the schedule keeps in `location.state` (ids + full
 * objects for the person pickers, `{ id, name }` objects for the checkbox lists), so the shared
 * `<SelectedFilters />` chip bar can chart it in controlled mode.
 */
export const MAP_FILTER_KEYS = {
    STAFFS: 'staffs',
    STAFF_FULL: 'staffFull',
    STAFF_STATUSES: 'staffStatuses',
    CLIENTS: 'clients',
    CLIENT_FULL: 'clientFull',
    CLIENT_STATUSES: 'clientStatuses',
    STATES: 'states',
    CITIES: 'cities',
};

/** Distinct marker colors — blue for clients, purple for staff (matches the design). */
export const MAP_COLORS = {
    [MAP_MARKER_TYPES.CLIENT]: '#2E90FA',
    [MAP_MARKER_TYPES.STAFF]: '#7F56D9',
};

/** Dashed line drawn from a clicked staff marker to each of their clients (purple, matches staff). */
export const MAP_CONNECTOR_COLOR = '#7F56D9';

/**
 * Hover hit width (px) of a connector line. The stroke itself is invisible (the dashes are icons),
 * so this only widens the area that picks up the mileage-on-hover tooltip.
 */
export const MAP_CONNECTOR_HIT_WEIGHT = 14;

/** Google Maps overlay mouse events used for the connector hover tooltip. */
export const MAP_POLY_EVENTS = {
    OVER: 'mouseover',
    MOVE: 'mousemove',
    OUT: 'mouseout',
};

/** Geographic center of the contiguous US — the fallback view before markers fit bounds. */
export const DEFAULT_MAP_CENTER = { lat: 39.8283, lng: -98.5795 };
export const DEFAULT_MAP_ZOOM = 4;
/** Cap the auto-zoom so a single marker (or tightly clustered ones) doesn't zoom to street level. */
export const MAX_FIT_ZOOM = 14;

/** Markers whose screen positions fall within this many pixels merge into one "+N" cluster bubble. */
export const MAP_CLUSTER_RADIUS_PX = 10;

/**
 * Ring radius (in degrees, ~24m) used to spread markers that share an EXACT coordinate — e.g. two
 * people at the same address. Identical points can never be pulled apart by zoom, so each is nudged
 * onto a small ring around the shared point. The offset is tiny enough to still read as "the same
 * place", but grows in screen pixels as you zoom: the group reads as one "+N" bubble when zoomed out
 * and separates into individual pins at street level — same behavior as any other cluster.
 */
export const MAP_COINCIDENT_SPREAD_DEG = 0.00022;

/**
 * True only for a real, plottable coordinate pair. Guards against un-mappable records the API can
 * still emit — empty-string coords (no geocoded address), NaN, and out-of-range values (e.g. a
 * lat/lng that was stored swapped, so latitude lands outside ±90). Plotting these corrupts the
 * auto-fit bounds and drops broken pins into the ocean, so they're filtered out before rendering.
 */
export const isPlottableCoord = (item) => {
    const lat = Number(item?.lat);
    const lng = Number(item?.lng);
    return (
        item?.lat !== '' &&
        item?.lng !== '' &&
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        Math.abs(lat) <= 90 &&
        Math.abs(lng) <= 180 &&
        !(lat === 0 && lng === 0)
    );
};

/**
 * Distinct, sorted `{ id, name }` options for one address field (`state` / `city`) across pins —
 * the State and City filters only offer places someone on the map actually lives or works.
 */
export const placeOptions = (items, field) =>
    [...new Set(items.map((i) => (i?.[field] || '').trim()).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ id: name, name }));

/** Pins whose address state is one of `states` — all of them when no state is picked. */
export const pinsInStates = (items, states) => (states.length ? items.filter((i) => states.includes((i?.state || '').trim())) : items);

/**
 * Drop picked cities that the picked states no longer offer — a city chip must never outlive the
 * state it belongs to, or the map filters to nothing with a chip the City list doesn't show.
 * Returns `filters` untouched when nothing has to go.
 */
export const pruneCities = (filters, pins) => {
    const cities = filters[MAP_FILTER_KEYS.CITIES];
    if (!Array.isArray(cities) || !cities.length) return filters;
    const states = (filters[MAP_FILTER_KEYS.STATES] || []).map((s) => s?.id ?? s);
    const offered = new Set(placeOptions(pinsInStates(pins, states), 'city').map((o) => o.id));
    const kept = cities.filter((c) => offered.has(c?.id ?? c));
    if (kept.length === cities.length) return filters;
    const next = { ...filters };
    if (kept.length) next[MAP_FILTER_KEYS.CITIES] = kept;
    else delete next[MAP_FILTER_KEYS.CITIES];
    return next;
};

/**
 * Straight-line (great-circle) distance between two {lat, lng} points, in miles.
 * Used for the "Assigned Clients" list — the distance from a staff member to each of their
 * clients. Returns null when either point lacks numeric coordinates.
 */
export const distanceMiles = (from, to) => {
    if (
        !from ||
        !to ||
        typeof from.lat !== 'number' ||
        typeof from.lng !== 'number' ||
        typeof to.lat !== 'number' ||
        typeof to.lng !== 'number'
    ) {
        return null;
    }
    const toRad = (deg) => (deg * Math.PI) / 180;
    const EARTH_RADIUS_MI = 3958.8;
    const dLat = toRad(to.lat - from.lat);
    const dLng = toRad(to.lng - from.lng);
    const a =
        Math.sin(dLat / 2) ** 2 + Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLng / 2) ** 2;
    return EARTH_RADIUS_MI * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/** "12.4 mi" for a `distanceMiles` result — the connector badge and the Assigned Clients list. Null when unknown. */
export const formatMiles = (miles) => (typeof miles === 'number' && Number.isFinite(miles) ? `${miles.toFixed(1)} mi` : null);

/** Where a shown distance came from: the road network (Routes API) or a straight line between pins. */
export const DISTANCE_KINDS = {
    DRIVING: 'DRIVING',
    STRAIGHT: 'STRAIGHT',
};

/** Destinations per Routes API matrix request (one origin) — well under its per-request element cap. */
export const ROUTE_MATRIX_MAX_DESTINATIONS = 25;

/**
 * Label + tooltip for a `bestDistance` result. Straight-line miles carry a "≈" so they never pass
 * for driving miles (they're always shorter). Null when there's no distance at all.
 */
export const distanceLabel = ({ miles, kind } = {}) => {
    const text = formatMiles(miles);
    if (!text) return null;
    return kind === DISTANCE_KINDS.DRIVING
        ? { text, title: 'Driving distance' }
        : { text: `≈ ${text}`, title: 'Straight-line distance (driving distance unavailable)' };
};

/**
 * Map styling: street and city names only. Turn off every label + icon, then bring back road name
 * text and city (locality) names, and hide POI/transit geometry entirely — so the map is roads,
 * street names and city names, nothing else.
 * (Inline `styles` is honored only when the map has no cloud `mapId`; this map sets none.)
 */
export const MAP_STYLES = [
    { elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'labels.text', stylers: [{ visibility: 'on' }] },
    { featureType: 'administrative.locality', elementType: 'labels.text', stylers: [{ visibility: 'on' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];
